
import { CreativeListModel, DefaultCreativeListModel } from '../CreativeList/CreativeListModel';
import { AddonFeatureManager, AgencyManager, DefaultAgencyManager } from 'core';
import { SessionStorageHelper, SessionStorageItemKeys } from 'helper/StorageHelper';
import { SelectOptions } from 'components/commonType';
import { UpdateEventListener, FireableUpdateEventListener } from 'utils/UpdateEventListener';

export interface CreativesHomeModel {
  readonly event: UpdateEventListener<CreativesHomeModel>;
  readonly advertiserId?: number;
  readonly advertisers: Array<SelectOptions>;
  readonly state: CreativesHomeModelState;
  init (): Promise<void>;
  setAdvertiserId (advertiserId: number | undefined): void;
  setAgencyId? (agencyId?: number): void;
  updateAdvertisers (advertisers: Array<SelectOptions>): void;
  getCreativeListModel (searchString: string | null, onSearchChange: (searchString: string) => void, creativeType?: string | null, onCreativeTypeChange?: (creativeType: number) => void): CreativeListModel;
}

export type CreativesHomeProps = {
  readonly model: CreativesHomeModel;
};

export type CreativesHomeModelState = {
  readonly advertiserId?: number;
  readonly agencyId?: number;
  readonly agencies?: Array<SelectOptions>;
  readonly loading: boolean;
};

export class DefaultCreativesHomeModel implements CreativesHomeModel {

  advertiserId?: number;
  advertisers: Array<SelectOptions>;
  creativeListModel?: CreativeListModel;
  addonFeatureManager: AddonFeatureManager;
  event: FireableUpdateEventListener<CreativesHomeModel>;
  loading: boolean;

  constructor (
    advertisers: Array<SelectOptions>,
    addonFeatureManager: AddonFeatureManager
  ) {
    this.event = new FireableUpdateEventListener<CreativesHomeModel>();
    this.advertiserId = SessionStorageHelper.getNumberItem(SessionStorageItemKeys.ADVERTISER);
    this.advertisers = advertisers;
    this.addonFeatureManager = addonFeatureManager;
    this.loading = false;
  }

  get isSysAdmin (): boolean {
    return false;
  }

  async init () {
    // This is intentional
  }

  get state (): CreativesHomeModelState {
    return {
      loading: this.loading,
      advertiserId: this.advertiserId
    };
  }

  setAdvertiserId (advertiserId: number | undefined) {
    const nextAdvertiserId = advertiserId === undefined ? undefined : Number(advertiserId);
    if (this.advertiserId === nextAdvertiserId) {
      return;
    }

    this.advertiserId = nextAdvertiserId;
    this.creativeListModel && this.creativeListModel.changeAdvertiser(this.advertiserId);
  }

  updateAdvertisers (advertisers: Array<SelectOptions>) {
    this.advertisers = advertisers;
    this.advertiserId = SessionStorageHelper.getNumberItem(SessionStorageItemKeys.ADVERTISER);
    this.event.fireEvent(this);
    this.creativeListModel && this.creativeListModel.changeAdvertiser(this.advertiserId);
  }

  getCreativeListModel (searchString: string | null, onSearchChange: (searchString: string) => void, creativeType?: string | null, onCreativeTypeChange?: (creativeType: number) => void): CreativeListModel {
    if (this.creativeListModel) {
      return this.creativeListModel;
    }

    this.creativeListModel = new DefaultCreativeListModel(searchString, onSearchChange, this.addonFeatureManager, undefined, creativeType, onCreativeTypeChange, this.isSysAdmin);
    return this.creativeListModel;
  }

  updateState (loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}

export class SysAdminCreativesHomeModel extends DefaultCreativesHomeModel {

  agencyManager: AgencyManager;
  agencies: Array<SelectOptions>;
  constructor (
    advertisers: Array<SelectOptions>,
    addonFeatureManager: AddonFeatureManager,
    private onAgencyChange: (agencyId) => void,
    agencyManager: AgencyManager = new DefaultAgencyManager()
  ) {
    super(advertisers, addonFeatureManager);
    this.agencies = [];
    this.agencyManager = agencyManager;
    this.loading = true;
  }

  get isSysAdmin (): boolean {
    return true;
  }

  get state () {
    return {
      loading: this.loading,
      agencyId: SessionStorageHelper.getNumberItem(SessionStorageItemKeys.AGENCY),
      advertiserId: this.advertiserId,
      agencies: this.agencies
    };
  }

  async init () {
    this.updateState(true);
    try {
      this.agencies = await this.agencyManager.getAgenciesOptions();
    } catch (e) {}
    this.updateState(false);
  }

  setAgencyId (agencyId?: number) {
    this.advertiserId = undefined;
    SessionStorageHelper.removeItem(SessionStorageItemKeys.ADVERTISER);
    this.onAgencyChange(agencyId);
  }
}
